import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { SerializableError } from '@/infrastructure/errors'

export type AppErrorType = 'ui' | 'api'

export interface AppErrorItem {
  id: string
  type: AppErrorType
  source?: string
  message: string
  statusCode?: number
  createdAt: number
}

function createErrorId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export const useErrorStore = defineStore('error', () => {
  const errors = ref<AppErrorItem[]>([])

  const getErrors = computed(() => errors.value)

  function pushError(error: Omit<AppErrorItem, 'id' | 'createdAt'>) {
    const last = errors.value[errors.value.length - 1]
    // Prevent duplicate spam from simultaneous retries.
    if (
      last &&
      last.type === error.type &&
      last.source === error.source &&
      last.message === error.message
    ) {
      return
    }

    errors.value.push({
      id: createErrorId(),
      createdAt: Date.now(),
      ...error,
    })
  }

  function setUIError(message: string, source?: string) {
    if (!message) return
    pushError({ type: 'ui', message, source })
  }

  function setApiError(error: SerializableError | Error | unknown, source?: string) {
    if (!error || typeof error !== 'object') return
    const message = (error as Record<string, unknown>).message
    if (typeof message !== 'string' || !message) return
    const statusCode = (error as Record<string, unknown>).statusCode

    pushError({
      type: 'api',
      source,
      message,
      statusCode: typeof statusCode === 'number' ? statusCode : undefined,
    })
  }

  function clearErrors() {
    errors.value = []
  }

  function removeError(id: string) {
    errors.value = errors.value.filter(error => error.id !== id)
  }

  function getErrorsBySource(source: string) {
    return errors.value.filter(error => error.source === source)
  }

  function clearErrorsBySource(source: string) {
    errors.value = errors.value.filter(error => error.source !== source)
  }

  return {
    errors,
    getErrors,
    setUIError,
    setApiError,
    clearErrors,
    removeError,
    getErrorsBySource,
    clearErrorsBySource,
  }
})
